import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';

import { PageDrawerFooter } from 'common/components/PageDrawer/PageDrawerFooter';
import { FilterBar } from 'common/components/FilterBar';
import { Loader } from 'common/components/Loader';
import { ICertificateTypeListItemFragment } from 'graphql/types';
import { Button } from 'common/components/Button';
import { IPageDrawerProps } from 'common/components/PageDrawer/PageDrawer';
import { PageDrawer } from 'common/components/PageDrawer';
import { Pagination } from 'common/components/Pagination';
import { useCertificateList } from 'certificate/hooks';

import { CertificateAddList } from './CertificateAddList';

interface IProps extends IPageDrawerProps {
    addedCertificates: ICertificateTypeListItemFragment[];
    selectedCertificates: ICertificateTypeListItemFragment[];
    onAddCertificates(certificates?: ICertificateTypeListItemFragment[]): void;
    onClose?(): void;
    onRemoveCertificate?(
        selectedCertificate: ICertificateTypeListItemFragment
    ): void;
    onSelectCertificate?(certificate: ICertificateTypeListItemFragment): void;
    setSelectedCertificates?(
        certificates: ICertificateTypeListItemFragment[]
    ): void;
}

export const CertificateSelectDrawer = ({
    addedCertificates,
    selectedCertificates,
    open,
    onAddCertificates,
    onClose,
    onRemoveCertificate,
    onSelectCertificate,
    setSelectedCertificates,
}: IProps) => {
    const [translate] = useTranslation();

    // Persist the drawer's filter state in sessionStorage rather than the URL
    // so it does not leak into the surrounding page.
    const {
        certificates,
        loading,
        paginationSettings,
        filters,
        filterValues,
        setPage,
        onSearch,
        onFilterChange,
    } = useCertificateList(false);

    const handleSearch = (searchValue: string) => {
        setPage(1);
        onSearch?.(searchValue);
    };

    const resetSettings = () => {
        handleSearch('');
        onFilterChange?.();
        setPage(1);
    };

    return (
        <PageDrawer
            open={open}
            title={translate('certificateSelector.title')}
            onClose={() => {
                onClose?.();

                // When selection is changed but drawer is closed, it needs to reset the selected certificates
                // else they are added, but not selected anymore
                setSelectedCertificates?.(addedCertificates);

                resetSettings();
            }}
        >
            <Box sx={{ pb: 22.5, pt: { xs: 2, sm: 4 }, px: { xs: 2, sm: 4 } }}>
                <FilterBar
                    useFilterDrawer
                    disabled={loading}
                    filterDrawerProps={{
                        title: translate('filterForm.certificates.title'),
                        description: translate(
                            'filterForm.certificates.description'
                        ),
                    }}
                    formFilters={filters}
                    formFilterValues={filterValues}
                    initialSearchValue={filterValues.q}
                    placeholder={translate('filterBarPlaceholder')}
                    onFiltersChange={(values) => {
                        setPage(1);

                        onFilterChange?.(values);
                    }}
                    onSearch={handleSearch}
                    onSearchClear={() => handleSearch('')}
                />

                {loading && <Loader />}

                {open && !loading && (
                    <CertificateAddList
                        addedCertificates={addedCertificates}
                        certificates={certificates}
                        selectedCertificates={selectedCertificates}
                        onAddCertificate={onSelectCertificate}
                        onRemoveCertificate={onRemoveCertificate}
                    />
                )}
            </Box>
            <PageDrawerFooter>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    {!loading && paginationSettings.count > -1 && (
                        <Box sx={{ mb: 2 }}>
                            <Pagination
                                page={paginationSettings.page}
                                pageAmount={paginationSettings.pageAmount}
                                totalsAmount={paginationSettings.count}
                                totalsText={translate('certificate', {
                                    count:
                                        paginationSettings.count >= 0
                                            ? paginationSettings.count
                                            : 0,
                                })}
                                onChange={(page: number) => {
                                    setPage(page);
                                }}
                            />
                        </Box>
                    )}

                    <Button
                        color="primary"
                        disabled={!selectedCertificates.length}
                        variant="contained"
                        onClick={() => {
                            onAddCertificates(selectedCertificates);
                            resetSettings();
                        }}
                    >
                        {!selectedCertificates.length
                            ? translate('certificateSelector.title')
                            : translate('certificateSelector.addButtonText', {
                                  count: selectedCertificates.length,
                              })}
                    </Button>
                </Box>
            </PageDrawerFooter>
        </PageDrawer>
    );
};
